// src/services/policies-service.js
// Policies — canonical Firestore location is practitioners/{uid}.policies.{cancellation|privacy|terms}.
// During the Phase 2 migration window, callers may read with fallback to the legacy nested location
// at website_published.contactFooter.{cancellationPolicy|privacyPolicy|termsOfService}.
// See docs/DF-IA-Restructure-Spec-May2026.md Decision 2 for the migration plan.

import { db } from '../config/firebase-config';
import { doc, getDoc, updateDoc } from 'firebase/firestore';

// Maps canonical policy name → legacy field name on website_published.contactFooter
const LEGACY_FIELD = {
  cancellation: 'cancellationPolicy',
  privacy: 'privacyPolicy',
  terms: 'termsOfService',
};

const POLICY_NAMES = Object.keys(LEGACY_FIELD);

/**
 * Read the policies map at the canonical path.
 * @param {string} uid
 * @returns {Promise<{cancellation?, privacy?, terms?} | null>} the policies object or null if not set
 */
export async function getPolicies(uid) {
  if (!uid) return null;
  const snap = await getDoc(doc(db, 'practitioners', uid));
  if (!snap.exists()) return null;
  return snap.data()?.policies || null;
}

/**
 * Read a single policy with fallback to the legacy location.
 * Tries canonical path first; on miss, falls back to website_published.contactFooter.{legacyName}.
 * @param {string} uid
 * @param {'cancellation' | 'privacy' | 'terms'} policyName
 * @returns {Promise<{data: object | null, source: 'canonical' | 'legacy' | 'none'}>}
 */
export async function getPolicyWithFallback(uid, policyName) {
  if (!uid || !LEGACY_FIELD[policyName]) {
    return { data: null, source: 'none' };
  }
  const snap = await getDoc(doc(db, 'practitioners', uid));
  if (!snap.exists()) return { data: null, source: 'none' };
  const data = snap.data() || {};

  const canonical = data.policies?.[policyName];
  if (canonical) return { data: canonical, source: 'canonical' };

  const legacy = data.website_published?.contactFooter?.[LEGACY_FIELD[policyName]];
  if (legacy) return { data: legacy, source: 'legacy' };

  return { data: null, source: 'none' };
}

/**
 * Save a policy at the canonical path.
 * Uses updateDoc with dotted-path notation — auto-creates the policies map if missing,
 * and avoids the setDoc(merge) + deleteField footgun documented in CLAUDE.md.
 * @param {string} uid
 * @param {'cancellation' | 'privacy' | 'terms'} policyName
 * @param {object} policyData
 */
export async function savePolicy(uid, policyName, policyData) {
  if (!uid) throw new Error('savePolicy: uid required');
  if (!LEGACY_FIELD[policyName]) {
    throw new Error(`savePolicy: invalid policyName "${policyName}". Must be one of: ${POLICY_NAMES.join(', ')}`);
  }
  const practRef = doc(db, 'practitioners', uid);
  await updateDoc(practRef, {
    [`policies.${policyName}`]: policyData,
  });
}

/**
 * One-time opportunistic migration from legacy nested location to canonical path.
 * Only copies policies where the canonical slot is empty AND legacy has data.
 * Leaves legacy data in place — read-fallback depends on it during the transition window.
 * Atomic: a single updateDoc covers all three policies; on failure none are migrated.
 * @param {string} uid
 * @returns {Promise<{migrated: string[], skipped: string[], errors: string[]}>}
 */
export async function migrateLegacyPolicies(uid) {
  const result = { migrated: [], skipped: [], errors: [] };
  if (!uid) {
    result.errors.push('migrateLegacyPolicies: uid required');
    return result;
  }

  let snap;
  try {
    snap = await getDoc(doc(db, 'practitioners', uid));
  } catch (err) {
    result.errors.push(`getDoc failed: ${err.message}`);
    return result;
  }
  if (!snap.exists()) {
    result.errors.push('Practitioner doc not found');
    return result;
  }

  const data = snap.data() || {};
  const existingPolicies = data.policies || {};
  const legacyContactFooter = data.website_published?.contactFooter || {};

  const updates = {};
  for (const name of POLICY_NAMES) {
    if (existingPolicies[name]) {
      result.skipped.push(name);
      continue;
    }
    const legacyValue = legacyContactFooter[LEGACY_FIELD[name]];
    if (legacyValue) {
      updates[`policies.${name}`] = legacyValue;
      result.migrated.push(name);
    } else {
      result.skipped.push(name);
    }
  }

  if (result.migrated.length > 0) {
    try {
      await updateDoc(doc(db, 'practitioners', uid), updates);
    } catch (err) {
      result.errors.push(`updateDoc failed: ${err.message}`);
      result.migrated = []; // atomic write failed — none migrated
    }
  }

  return result;
}

/**
 * Setup-checklist signal for Decision 4 / Item 4.
 * Returns true when the cancellation policy is set at the canonical path.
 * Per the spec, cancellation is the primary signal for "policies started" because it's
 * the most operationally relevant of the three; Privacy/Terms can be added later.
 * @param {string} uid
 * @returns {Promise<boolean>}
 */
export async function hasCancellationPolicy(uid) {
  if (!uid) return false;
  const policies = await getPolicies(uid);
  return !!policies?.cancellation;
}
